/**
 * Transmission Km & Compteur — le relevé mensuel d'un chauffeur.
 *
 * *Demande de Chris, 21/09/2026 : « ce sera pour que les chauffeurs puissent envoyer le compteur
 * une fois par mois avec leur permis à côté du compteur, l'idée est de prendre une photo, le
 * compteur ou km doit être automatiquement récupéré à partir de la photo et si pas bien récupéré
 * le chauffeur ou conducteur de l'engin doit saisir. La date et heure doit se mettre
 * automatiquement avec la photo. »*
 *
 * ## Trois gestes, dans cet ordre
 *
 * **La machine, la photo, le nombre.** La photo avant le nombre est le point : c'est elle qui
 * propose le chiffre, et lui demander de saisir d'abord ferait taper un nombre pour se le voir
 * remplacer.
 *
 * ## Trois chemins vers la machine, un seul aboutissement
 *
 * *Demande de Chris, 21/09/2026 : « dans km et compteur on doit pouvoir aussi retrouver la machine
 * en scannant qr code ou saisie du code avec assistance ou libellé ».*
 *
 * Le scan de l'étiquette, la recherche assistée (code, libellé, plaque) et le code tapé à la main
 * passent tous par `retenir` : une seule fonction garde la machine et propose son unité. Trois
 * chemins d'écriture, ce sont trois endroits où une règle peut manquer — c'est la leçon du 16/08,
 * et le module de stock l'applique déjà à ses trois façons d'arriver sur un article.
 *
 * Le scan est **en premier** parce que c'est le geste le plus rapide devant la machine, et un
 * chauffeur qui relève son compteur y est toujours.
 *
 * ## L'OCR propose, il ne décide pas
 *
 * Le champ est **prérempli et modifiable**, jamais verrouillé, et il l'est même quand la lecture a
 * réussi. Un cadran à sept segments se lit mal : la lecture échouera souvent, et c'est assumé —
 * Chris a posé lui-même la règle (*« si pas bien récupéré […] doit saisir »*). L'écran dit donc
 * toujours ce qu'il a cru lire, pour qu'on puisse le corriger en connaissance de cause plutôt que
 * de découvrir un chiffre venu de nulle part.
 *
 * Quand rien n'a été lu, l'écran ne s'excuse pas : il demande simplement le nombre. Une photo
 * illisible est le cas courant, pas une panne.
 *
 * ## La date et l'heure ne s'affichent qu'en lecture
 *
 * Elles viennent du **serveur**, au moment où la photo est déposée. Ni l'horloge du téléphone ni
 * la date EXIF ne sont retenues — la première se règle à la main, la seconde se recopie avec le
 * fichier —, et un relevé antidatable ne prouverait plus rien.
 *
 * ## Le permis sur la photo
 *
 * *« avec leur permis à côté du compteur »* : c'est une donnée personnelle, elle reste sur le
 * serveur du groupe et ne part dans aucun service tiers — la lecture automatique tourne sur cette
 * machine. L'écran le rappelle au chauffeur, une fois, sous le bouton.
 *
 * ## Écrit pour le téléphone
 *
 * C'est un écran de cabine : une colonne, des cibles larges, aucun tableau à faire défiler de
 * côté. La liste des derniers relevés passe en cartes sous 900 px, comme la pile des incidents.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { lireQrDansImage } from "./scan";
import {
  chargerReleves,
  chercherMachine,
  chercherMachines,
  deposerPhotoCompteur,
  enregistrerReleve,
} from "./api";
import type { MachineTrouvee, Releve } from "./api";

/** Ce que la photo déposée a rendu, tant que le relevé n'est pas enregistré. */
type Depot = {
  readonly photo: string;
  readonly priseLe: string;
  readonly lecture: number | null;
  readonly lectureBrute: string | null;
};

export function Compteurs(): JSX.Element {
  const [recherche, setRecherche] = useState("");
  const [propositions, setPropositions] = useState<readonly MachineTrouvee[]>([]);
  const [machine, setMachine] = useState<MachineTrouvee | null>(null);
  const frappe = useRef(0);

  const [depot, setDepot] = useState<Depot | null>(null);
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [valeur, setValeur] = useState("");
  const [unite, setUnite] = useState<"km" | "heures">("km");

  const [releves, setReleves] = useState<readonly Releve[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState<Releve | null>(null);
  const champPhoto = useRef<HTMLInputElement>(null);
  const champQr = useRef<HTMLInputElement>(null);
  const [scanEnCours, setScanEnCours] = useState(false);
  const [erreurScan, setErreurScan] = useState<string | null>(null);

  const recharger = useCallback((): void => {
    chargerReleves()
      .then((r) => {
        setReleves(r.releves);
      })
      .catch(() => {
        // L'historique est un confort : son échec ne doit pas empêcher d'envoyer un relevé.
      });
  }, []);

  useEffect(recharger, [recharger]);

  /** Propose les machines au fur et à mesure — même mécanisme que la remontée d'incident. */
  const proposer = useCallback((saisi: string): void => {
    const mien = ++frappe.current;
    if (saisi.trim().length < 2) {
      setPropositions([]);
      return;
    }
    chercherMachines(saisi.trim())
      .then((r) => {
        if (frappe.current !== mien) return;
        setPropositions(r.machines);
      })
      .catch(() => {
        if (frappe.current !== mien) return;
        setPropositions([]);
      });
  }, []);

  /**
   * Retient la machine choisie, d'où qu'elle vienne — scan, liste, ou code tapé.
   *
   * L'unité est **proposée** d'après l'immatriculation : un engin sans plaque compte le plus
   * souvent des heures, un camion des kilomètres. Ce n'est **pas** une règle du métier — 72
   * machines du parc n'ont pas d'immatriculation, ce qui ne prouve rien sur leur cadran —, donc le
   * champ reste visible et modifiable, et c'est la personne qui tranche.
   */
  const retenir = (m: MachineTrouvee): void => {
    setMachine(m);
    setPropositions([]);
    setErreurScan(null);
    setUnite(m.immatriculation === null ? "heures" : "km");
  };

  /**
   * Lit l'étiquette QR et retrouve la machine.
   *
   * Le texte lu part au serveur **tel quel** : c'est lui qui en extrait le code ERP et jette le
   * domaine, par la même route que la remontée d'incident. Une règle d'extraction est une règle
   * métier — elle se vérifie par des cas de test, elle ne vit pas dans un écran, et surtout elle
   * ne doit pas exister en deux exemplaires qui finiraient par diverger.
   */
  const scannerEtiquette = (fichier: File | undefined): void => {
    if (fichier === undefined) return;
    setScanEnCours(true);
    setErreurScan(null);
    lireQrDansImage(fichier)
      .then(async (texte) => {
        if (texte === null) {
          setErreurScan(
            "Aucun QR lu sur la photo. Cadrez l'étiquette de plus près, à plat et bien éclairée — " +
              "ou cherchez la machine par son code ou son libellé.",
          );
          return;
        }
        const { machine: fiche, code } = await chercherMachine(texte);
        retenir({
          id: fiche.id,
          codeErp: code,
          description: fiche.description,
          type: fiche.type,
          immatriculation: fiche.immatriculation,
          horsParc: fiche.horsParc,
        });
      })
      .catch((x: unknown) => {
        setErreurScan(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setScanEnCours(false);
        // Sans cette remise à zéro, rescanner DEUX fois la même étiquette ne déclenche rien :
        // l'input ne signale pas de changement si le fichier choisi est identique.
        if (champQr.current !== null) champQr.current.value = "";
      });
  };

  const deposer = (fichier: File | undefined): void => {
    if (fichier === undefined) return;
    setDepotEnCours(true);
    setErreur(null);
    setEnregistre(null);
    deposerPhotoCompteur(fichier)
      .then((r) => {
        setDepot(r);
        /*
          Prérempli SEULEMENT si le champ est vide.

          Sans ce garde-fou, reprendre une photo après avoir corrigé le nombre à la main écraserait
          la correction par une lecture qu'on venait justement de juger fausse.
        */
        setValeur((actuel) =>
          actuel.trim() === "" && r.lecture !== null ? String(r.lecture) : actuel,
        );
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setDepotEnCours(false);
        // Sans cette remise à zéro, reprendre DEUX fois la même photo ne déclenche rien.
        if (champPhoto.current !== null) champPhoto.current.value = "";
      });
  };

  const envoyer = (): void => {
    if (machine === null) return;
    setEnvoi(true);
    setErreur(null);
    enregistrerReleve({
      materielId: machine.id,
      valeur: valeur.trim(),
      unite,
      photo: depot?.photo,
      lecture: depot?.lecture ?? null,
      lectureBrute: depot?.lectureBrute ?? null,
    })
      .then(({ releve }) => {
        setEnregistre(releve);
        setMachine(null);
        setRecherche("");
        setValeur("");
        setDepot(null);
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setEnvoi(false);
      });
  };

  /** Ce qui empêche d'envoyer, en une phrase — un bouton grisé sans raison se lit comme une panne. */
  const ceQuiManque =
    machine === null
      ? "Choisissez la machine."
      : valeur.trim() === ""
        ? "Indiquez le compteur."
        : null;

  return (
    <section className="or compteurs">
      <p className="note">
        Une fois par mois : photographiez le compteur avec votre permis à côté, vérifiez le nombre
        lu, et envoyez. La date et l&apos;heure sont posées automatiquement.
      </p>

      {enregistre !== null && (
        <p className="annonce">
          <strong>Relevé enregistré</strong> sur {enregistre.materielCode} :{" "}
          {enregistre.valeur} {enregistre.unite}, le{" "}
          {new Date(enregistre.releveLe).toLocaleString("fr-FR")}.
        </p>
      )}

      {/*
        1. La machine — TROIS chemins, et le même aboutissement.

        *Demande de Chris, 21/09/2026 : « dans km et compteur on doit pouvoir aussi retrouver la
        machine en scannant qr code ou saisie du code avec assistance ou libellé ».*

        Le scan de l'étiquette, la recherche assistée, et le code tapé à la main mènent tous à la
        même machine choisie : il n'y a qu'un état à la sortie, donc une seule chose à apprendre et
        un seul endroit où un défaut peut se cacher. C'est la règle du module de stock, qui a trois
        chemins vers un article depuis le 07/09.

        Le scan d'abord, parce que c'est le geste le plus rapide quand on est devant la machine —
        et un chauffeur qui relève son compteur l'est toujours.
      */}
      <div className="or__lecture">
        <label className="or__photo">
          {/* `capture="environment"` demande l'appareil photo ARRIÈRE : avec le frontal, on ne
              photographie pas une étiquette collée sur un engin. */}
          <input
            ref={champQr}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              scannerEtiquette(e.target.files?.[0]);
            }}
          />
          <span>📷 Scanner l&apos;étiquette de la machine</span>
        </label>
      </div>
      {scanEnCours && <p className="annonce">Lecture de l&apos;étiquette…</p>}
      {erreurScan !== null && <p className="alerte">{erreurScan}</p>}

      <label>
        <span>ou cherchez-la</span>
        <input
          type="text"
          value={machine === null ? recherche : `${machine.codeErp} — ${machine.description ?? ""}`}
          placeholder="son code, ou « pelle », une plaque…"
          autoComplete="off"
          readOnly={machine !== null}
          onChange={(e) => {
            setRecherche(e.target.value);
            proposer(e.target.value);
          }}
        />
      </label>
      {machine !== null && (
        <button
          type="button"
          className="lien"
          onClick={() => {
            setMachine(null);
            setPropositions([]);
          }}
        >
          changer de machine
        </button>
      )}
      {machine === null && propositions.length > 0 && (
        <ul className="or__propositions">
          {propositions.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => {
                  // Le même chemin que le scan : un seul endroit qui retient la machine et
                  // propose son unité, donc un seul endroit où un défaut peut se cacher.
                  retenir(m);
                }}
              >
                <code>{m.codeErp}</code>
                <span>
                  {m.description ?? m.type ?? "—"}
                  {m.immatriculation === null ? "" : ` · ${m.immatriculation}`}
                  {m.horsParc && <strong> · hors parc</strong>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 2. La photo */}
      <div className="or__lecture">
        <label className="or__photo">
          <input
            ref={champPhoto}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              deposer(e.target.files?.[0]);
            }}
          />
          <span>📷 Photographier le compteur et le permis</span>
        </label>
      </div>
      <p className="note">
        La photo reste sur le serveur du groupe : la lecture du compteur se fait ici, elle ne part
        dans aucun service extérieur.
      </p>

      {depotEnCours && <p className="annonce">Lecture de la photo…</p>}
      {depot !== null && (
        <p className="annonce">
          Photo reçue le {new Date(depot.priseLe).toLocaleString("fr-FR")}.{" "}
          {depot.lecture === null ? (
            <>
              <strong>Le compteur n&apos;a pas pu être lu</strong> — saisissez-le ci-dessous.
            </>
          ) : (
            <>
              Compteur lu : <strong>{depot.lecture}</strong>. Vérifiez-le, et corrigez s&apos;il est
              faux.
            </>
          )}
        </p>
      )}

      {/* 3. Le nombre */}
      <label>
        <span>Compteur</span>
        <span className="or__compteur">
          <input
            type="text"
            inputMode="decimal"
            value={valeur}
            placeholder="ex. 147852"
            onChange={(e) => {
              setValeur(e.target.value);
            }}
          />
          <select
            value={unite}
            onChange={(e) => {
              setUnite(e.target.value === "heures" ? "heures" : "km");
            }}
          >
            <option value="km">km</option>
            <option value="heures">heures</option>
          </select>
        </span>
      </label>

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {ceQuiManque !== null && <p className="note">{ceQuiManque}</p>}

      <div className="fiche__actions">
        <button type="button" disabled={envoi || ceQuiManque !== null} onClick={envoyer}>
          {envoi ? "Envoi…" : "Envoyer le relevé"}
        </button>
      </div>

      <h2>Derniers relevés</h2>
      {releves.length === 0 ? (
        <p className="note">Aucun relevé pour le moment.</p>
      ) : (
        <table className="incidents__table">
          <thead>
            <tr>
              <th>Machine</th>
              <th>Compteur</th>
              <th>Le</th>
              <th>Par</th>
              <th>Lecture</th>
            </tr>
          </thead>
          <tbody>
            {releves.map((r) => (
              <tr key={r.id}>
                <td data-colonne="Machine">
                  <code>{r.materielCode}</code>
                </td>
                <td data-colonne="Compteur">
                  {r.valeur} {r.unite}
                </td>
                <td data-colonne="Le">{new Date(r.releveLe).toLocaleString("fr-FR")}</td>
                <td data-colonne="Par">{r.saisiParLibelle}</td>
                {/*
                  D'où vient le nombre. Affiché, et pas seulement stocké : c'est ce qui permettra de
                  dire dans six mois si la lecture automatique fait gagner du temps — une question
                  qui se mesure au lieu de se discuter.
                */}
                <td data-colonne="Lecture">
                  {r.origine === "ocr"
                    ? "lue sur la photo"
                    : r.origine === "corrige"
                      ? `corrigée (lu ${r.valeurLue ?? "?"})`
                      : "saisie"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
